import React, { Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAuth } from '../../context/AuthContext';
import { chatService, type DesignTier, type ExtremeDesignInfo } from '../../services/chatService';
import { getPricingPlanRank } from '../../utils/pricingPlans';

import { StandardDesignVisual, ExtremeDesignVisual } from './DesignTierVisuals';
import './DesignTierChooser.css';

// Loaded on demand: the billing modal pulls in the payment services.
const UpgradeModal = React.lazy(() => import('../BillingModal').then((module) => ({ default: module.UpgradeModal })));

const EXTREME_MIN_PLAN_RANK = getPricingPlanRank('plus');
const UPGRADE_SUBTITLE = 'Extreme design requires the Plus plan. Upgrade to design your landing page '
  + 'as a cinematic, scroll-driven 3D experience.';

const formatMultiplier = (value?: number) => (value ? `${Number(value.toFixed(2))}×` : '20×');

/** Server eligibility and price, falling back to the cached plan while it loads or if it fails. */
const useExtremeDesignInfo = () => {
  const { user } = useAuth();
  const [info, setInfo] = useState<ExtremeDesignInfo | null>(null);

  useEffect(() => {
    let live = true;

    chatService.getExtremeDesignInfo().then((data) => { if (live) setInfo(data); }).catch(() => undefined);

    return () => { live = false; };
  }, []);

  return {
    info,
    eligible: info?.eligible ?? getPricingPlanRank(user?.subscription_tier) >= EXTREME_MIN_PLAN_RANK,
  };
};

const useEscapeToClose = (onClose: () => void, active: boolean) => {
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [active, onClose]);
};

const TierOptions = (
  { info, eligible, standardCountdown, onStandard, onExtreme }: {
    info: ExtremeDesignInfo | null; eligible: boolean; standardCountdown: number | null;
    onStandard: () => void; onExtreme: () => void;
  },
) => {
  const firstButton = useRef<HTMLButtonElement>(null);

  useEffect(() => { firstButton.current?.focus(); }, []);

  return (
    <div className="design-tier-options">
      <button ref={firstButton} type="button" className="design-tier-option" onClick={onStandard}
        aria-describedby={standardCountdown === null ? undefined : 'design-tier-standard-countdown'}>
        <span className="design-tier-visual" aria-hidden="true"><StandardDesignVisual /></span>
        <span className="design-tier-name">Standard design</span>
        <span className="design-tier-desc">
          A clean, responsive landing page built from your requirements: a hero, feature sections and a footer that
          fit desktop and phone.
        </span>
        <span className="design-tier-cost">Standard section credits</span>
        {standardCountdown !== null && (
          <span className="design-tier-countdown" id="design-tier-standard-countdown" role="timer">
            Starts on its own in {standardCountdown}s
          </span>
        )}
      </button>
      <button
        type="button"
        className="design-tier-option design-tier-option--extreme"
        onClick={onExtreme}
        aria-describedby="design-tier-extreme-cost"
      >
        <span className="design-tier-visual" aria-hidden="true"><ExtremeDesignVisual /></span>
        <span className="design-tier-name">Extreme design <span className="design-tier-badge">Plus</span></span>
        <span className="design-tier-desc">
          A cinematic 3D landing page: your product as a live 3D model. As visitors scroll, the camera moves around
          it, its parts come apart and each part gets its own short story.
        </span>
        <span className="design-tier-cost" id="design-tier-extreme-cost">
          {formatMultiplier(info?.section_credit_multiplier)} credits per section{eligible ? '' : ' · requires Plus'}
        </span>
      </button>
    </div>
  );
};

/**
 * Asks how the first (landing) page should be designed before "Generate First Page" runs.
 * Extreme is a Plus-plan option; below Plus it opens the upgrade modal instead, and the countdown is stood down
 * (``onUpgrade``) so Standard does not start while the user looks at plans. While the server's countdown runs,
 * Standard shows the seconds left: it starts on its own unless the user picks first (20 Oct 2026, user).
 */
export function DesignTierChooser(
  { onChoose, onClose, standardCountdown = null, onUpgrade }: {
    onChoose: (tier: DesignTier) => void;
    onClose: () => void;
    /** Seconds until the server presses Generate First Page with Standard design, or null when it will not. */
    standardCountdown?: number | null;
    onUpgrade?: () => void;
  },
) {
  const { info, eligible } = useExtremeDesignInfo();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEscapeToClose(onClose, !upgradeOpen);

  if (upgradeOpen) {
    return (
      <Suspense fallback={null}>
        <UpgradeModal
          onlyHigherPlans
          subtitle={UPGRADE_SUBTITLE}
          onClose={() => { setUpgradeOpen(false); onClose(); }}
        />
      </Suspense>
    );
  }

  return createPortal(
    <div className="design-tier-backdrop" role="presentation" onClick={onClose}>
      <div className="design-tier-dialog" role="dialog" aria-modal="true" aria-labelledby="design-tier-title"
        onClick={(event) => event.stopPropagation()}>
        <h2 id="design-tier-title" className="design-tier-title">How should we design your landing page?</h2>
        <p className="design-tier-subtitle">This applies to the first page only. Other pages use the standard design.</p>
        <TierOptions info={info} eligible={eligible} standardCountdown={standardCountdown}
          onStandard={() => onChoose('basic')}
          onExtreme={() => {
            if (eligible) {
              onChoose('extreme');

              return;
            }
            onUpgrade?.();
            setUpgradeOpen(true);
          }} />
        <button type="button" className="design-tier-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>,
    document.body,
  );
}
